import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "./FormControls";

export interface MenuItem {
  label: ReactNode;
  /** 누르면 실행. to 가 있으면 링크로 간다 */
  onSelect?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  /** 항목 아래 작은 설명 */
  hint?: ReactNode;
}

/**
 * 드롭다운 메뉴 , 버튼 하나에 동작 여럿을 접는다.
 * 바깥을 누르거나 Esc 로 닫히고, 항목을 고르면 닫힌다. 모양은 globals.css 의 .menu 계열이 정한다.
 */
export function Menu({ trigger, triggerClassName, items, head, align = "end", placement = "down", label }: {
  /** 버튼 안 내용 */
  trigger: ReactNode;
  /** 버튼 차림새 , 안 주면 보조 버튼(control-button-secondary) */
  triggerClassName?: string;
  items: MenuItem[];
  /** 메뉴 맨 위 한 줄(계정 이메일 같은 설명) */
  head?: ReactNode;
  align?: "start" | "end";
  /** 트리거가 화면 하단에 붙어 있을 때 메뉴를 위로 연다 */
  placement?: "down" | "up";
  /** 버튼의 접근성 이름 */
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerProps = {
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    "aria-controls": id,
    "aria-label": label,
    onClick: () => setOpen((v) => !v),
  };
  const triggerContent = <>{trigger}<svg viewBox="0 0 16 16" className="size-icon-small shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden><path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg></>;

  return (
    <div ref={wrap} className="menu-wrap">
      {triggerClassName
        ? <button type="button" className={triggerClassName} {...triggerProps}>{triggerContent}</button>
        : <Button {...triggerProps}>{triggerContent}</Button>}
      {open && (
        <div
          id={id}
          role="menu"
          className={`menu ${placement === "up" ? "menu-up" : "menu-down"} ${align === "end" ? "menu-end" : "menu-start"}`}
        >
          {head && <div className="menu-head">{head}</div>}
          {head && <div className="menu-sep" />}
          {items.map((it, i) => {
            const cls = `menu-item${it.danger ? " menu-item-danger" : ""}`;
            const body = (
              <span className="flex min-w-0 flex-col">
                <span>{it.label}</span>
                {it.hint && <span className="text-footnote text-ink-subtle">{it.hint}</span>}
              </span>
            );
            if (it.to && !it.disabled) {
              return (
                <Link key={i} role="menuitem" to={it.to} className={cls} onClick={() => setOpen(false)}>
                  {body}
                </Link>
              );
            }
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={cls}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
